import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { bookingAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUS_COLOR = {
  pending: '#f59e0b', confirmed: '#10b981',
  in_progress: '#3b82f6', completed: '#6366f1',
  cancelled: '#ef4444', disputed: '#dc2626'
};

const STATUS_LABEL = {
  pending: '⏳ Pending — Decorator ka wait karo',
  confirmed: '✅ Confirmed — Baat karo aur details fix karo',
  in_progress: '🎨 Kaam chal raha hai',
  completed: '🎉 Completed!',
  cancelled: '❌ Cancelled',
  disputed: '⚠️ Disputed'
};

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [review, setReview] = useState({ rating: 5, comment: '' });
  const [reviewing, setReviewing] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  const fetchBooking = () => {
    bookingAPI.getOne(id)
      .then(({ data }) => setBooking(data))
      .catch(() => toast.error('Booking nahi mili'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBooking(); }, [id]);

  const handleCancel = async () => {
    if (!window.confirm('Booking cancel karna chahte ho?')) return;
    setCancelling(true);
    try {
      await bookingAPI.cancel(id, { reason: 'Client ne cancel kiya' });
      toast.success('Booking cancel ho gayi');
      fetchBooking();
    } catch { toast.error('Cancel nahi hua'); }
    finally { setCancelling(false); }
  };

  if (loading) return <div className="page"><div className="spinner" /></div>;
  if (!booking) return <div className="page"><p>Booking nahi mili</p></div>;

  const isClient = user?.role === 'client';
  const isDecorator = user?.role === 'decorator';
  const otherPerson = isClient ? booking.decorator?.user : booking.client;

  return (
    <div className="page">
      <style>{`
        .bd-container { max-width: 700px; margin: 0 auto; padding: 16px; }
        .bd-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 10px 0; border-bottom: 1px solid #f1f5f9; gap: 8px; }
        .bd-label { color: #64748b; font-size: 14px; flex-shrink: 0; }
        .bd-value { font-weight: 600; font-size: 14px; text-align: right; word-break: break-word; max-width: 60%; }
        .bd-card { background: white; border-radius: 12px; padding: 16px; margin-bottom: 16px; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }
        .bd-card h3 { margin: 0 0 14px; font-size: 16px; }
        .commission-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 14px; }
        .commission-tile { text-align: center; padding: 10px 6px; background: white; border-radius: 10px; }
        .commission-tile .big { font-size: 22px; font-weight: 800; }
        .commission-tile .small { font-size: 11px; color: #64748b; margin-top: 2px; }
        .star-row { display: flex; gap: 6px; margin-bottom: 14px; flex-wrap: wrap; align-items: center; }
        .star { font-size: 32px; cursor: pointer; transition: color 0.2s; }
        .btn-full { width: 100%; display: flex; justify-content: center; padding: 13px; }
        @media (max-width: 480px) {
          .bd-container { padding: 12px; }
          .commission-grid { grid-template-columns: 1fr 1fr 1fr; gap: 6px; }
          .commission-tile .big { font-size: 18px; }
          .bd-value { max-width: 55%; font-size: 13px; }
          .bd-label { font-size: 13px; }
          .star { font-size: 28px; }
        }
      `}</style>

      <div className="bd-container">

        {/* Back Button */}
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#64748b', fontSize: '14px', marginBottom: '16px', padding: 0
        }}>← Wapas Jaao</button>

        {/* Status Banner */}
        <div style={{
          padding: '14px 16px', borderRadius: '12px', marginBottom: '16px',
          background: STATUS_COLOR[booking.status] + '15',
          border: `1px solid ${STATUS_COLOR[booking.status]}40`
        }}>
          <p style={{ color: STATUS_COLOR[booking.status], fontWeight: 700, fontSize: '15px', margin: 0 }}>
            {STATUS_LABEL[booking.status]}
          </p>
          <p style={{ color: '#64748b', fontSize: '12px', margin: '4px 0 0' }}>
            Booking #{booking.bookingNumber}
          </p>
        </div>

        {/* Booking Details */}
        <div className="bd-card">
          <h3>📋 Booking Details</h3>
          {[
            ['Event', booking.eventType],
            ['Date', new Date(booking.eventDate).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' })],
            ['Venue', (booking.venue?.address || '') + ', ' + (booking.venue?.city || '')],
            ['Guests', booking.guestCount || 'Bataya nahi'],
            ['Requirements', booking.requirements || 'Koi special requirement nahi'],
          ].map(([label, value]) => (
            <div key={label} className="bd-row">
              <span className="bd-label">{label}</span>
              <span className="bd-value">{value}</span>
            </div>
          ))}
        </div>

        {/* Amount Details */}
        <div className="bd-card">
          <h3>💰 Amount Details</h3>
          <div className="bd-row">
            <span className="bd-label">Total Amount</span>
            <strong>₹{booking.totalAmount?.toLocaleString()}</strong>
          </div>
          {booking.advanceAmount > 0 ? (
            <>
              <div className="bd-row">
                <span className="bd-label">Advance (Cash)</span>
                <strong style={{ color: '#f59e0b' }}>₹{booking.advanceAmount?.toLocaleString()}</strong>
              </div>
              <div className="bd-row">
                <span className="bd-label">Baaki Event Pe</span>
                <strong>₹{(booking.totalAmount - booking.advanceAmount)?.toLocaleString()}</strong>
              </div>
            </>
          ) : (
            <div style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ color: '#64748b', fontSize: '13px' }}>💵 Poora payment event ke din cash mein</span>
            </div>
          )}
          <div style={{ padding: '10px 12px', background: '#ecfdf5', borderRadius: '8px', marginTop: '10px', fontSize: '13px' }}>
            ✅ <strong style={{ color: '#10b981' }}>Platform charge: Rs.0 — Client ke liye FREE</strong>
          </div>
        </div>

        {/* Commission Info — Sirf Decorator ko */}
        {isDecorator && (
          <div className="bd-card" style={{ background: '#fffbeb', border: '1px solid #fcd34d' }}>
            <h3 style={{ color: '#92400e' }}>📊 Commission Breakdown</h3>
            <div className="commission-grid">
              <div className="commission-tile">
                <div className="big" style={{ color: '#ef4444' }}>3%</div>
                <div className="small">Platform</div>
              </div>
              <div className="commission-tile">
                <div className="big" style={{ color: '#10b981' }}>97%</div>
                <div className="small">Tumhara</div>
              </div>
              <div className="commission-tile">
                <div className="big" style={{ color: '#3b82f6', fontSize: '16px' }}>
                  ₹{booking.decoratorAmount?.toLocaleString()}
                </div>
                <div className="small">Net Amount</div>
              </div>
            </div>
            <div className="bd-row">
              <span style={{ color: '#78350f', fontSize: '14px' }}>Total Amount</span>
              <strong>₹{booking.totalAmount?.toLocaleString()}</strong>
            </div>
            <div className="bd-row">
              <span style={{ color: '#78350f', fontSize: '14px' }}>Commission (3%)</span>
              <strong style={{ color: '#dc2626' }}>-₹{booking.commissionAmount?.toLocaleString()}</strong>
            </div>
            <div className="bd-row" style={{ borderBottom: 'none' }}>
              <span style={{ color: '#78350f', fontWeight: 700, fontSize: '14px' }}>Tumhara Paisa</span>
              <strong style={{ color: '#16a34a', fontSize: '17px' }}>₹{booking.decoratorAmount?.toLocaleString()}</strong>
            </div>
            <div style={{
              marginTop: '12px', padding: '10px 12px',
              background: '#fef3c7', borderRadius: '8px', fontSize: '13px', color: '#92400e'
            }}>
              ⚠️ Event complete hone ke baad ₹{booking.commissionAmount?.toLocaleString()} admin ko cash mein dena hoga.
            </div>
          </div>
        )}

        {/* Other Person Info + Chat */}
        {otherPerson && (
          <div className="bd-card">
            <h3>{isClient ? '🎨 Decorator Info' : '👤 Client Info'}</h3>
            <p style={{ fontWeight: 600, margin: '0 0 4px' }}>
              {isClient ? booking.decorator?.businessName : otherPerson?.name}
            </p>
            <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>📞 {otherPerson?.phone}</p>
            {['confirmed', 'in_progress'].includes(booking.status) && (
              <Link to={`/chat`} className="btn btn-primary"
                style={{ marginTop: '14px', display: 'inline-flex' }}>
                💬 Chat Karo
              </Link>
            )}
          </div>
        )}

        {/* Cancel Button */}
        {booking.status === 'pending' && isClient && (
          <button className="btn btn-outline btn-full"
            style={{ borderColor: '#ef4444', color: '#ef4444', marginBottom: '16px' }}
            onClick={handleCancel} disabled={cancelling}>
            {cancelling ? 'Cancel ho raha hai...' : '❌ Booking Cancel Karo'}
          </button>
        )}

        {/* Review Form */}
        {isClient && booking.status === 'completed' && !reviewSubmitted && (
          <div className="bd-card" style={{ border: '2px solid #f59e0b', background: '#fffbeb' }}>
            <h3 style={{ color: '#92400e' }}>⭐ Decorator ko Rate Karo</h3>
            <div className="star-row">
              {[1, 2, 3, 4, 5].map(star => (
                <span key={star} className="star"
                  onClick={() => setReview({ ...review, rating: star })}
                  style={{ color: star <= review.rating ? '#f59e0b' : '#e2e8f0' }}>★</span>
              ))}
              <span style={{ color: '#64748b', fontSize: '13px' }}>
                {['', 'Bahut Bura', 'Theek Tha', 'Acha Tha', 'Bahut Acha', 'Zabardast!'][review.rating]}
              </span>
            </div>
            <textarea className="input" rows={3}
              placeholder="Kaam kaisa tha? Decoration kaisi lagi? Dusron ko recommend karoge?"
              value={review.comment}
              onChange={e => setReview({ ...review, comment: e.target.value })}
              style={{ marginBottom: '12px', width: '100%', boxSizing: 'border-box' }}
            />
            <button className="btn btn-primary btn-full"
              disabled={reviewing || !review.comment}
              onClick={async () => {
                setReviewing(true);
                try {
                  const { reviewAPI } = await import('../utils/api');
                  await reviewAPI.create({
                    decoratorId: booking.decorator?._id,
                    bookingId: booking._id,
                    rating: review.rating,
                    comment: review.comment
                  });
                  toast.success('Review de diya! Shukriya ⭐');
                  setReviewSubmitted(true);
                } catch (err) {
                  toast.error(err?.response?.data?.message || 'Review nahi hua');
                } finally { setReviewing(false); }
              }}>
              {reviewing ? 'Submit ho raha hai...' : '⭐ Review Submit Karo'}
            </button>
          </div>
        )}

        {isClient && booking.status === 'completed' && reviewSubmitted && (
          <div className="bd-card" style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', textAlign: 'center' }}>
            <p style={{ fontSize: '28px', margin: '0 0 8px' }}>⭐</p>
            <p style={{ fontWeight: 700, color: '#065f46', margin: 0 }}>Review de diya! Shukriya</p>
          </div>
        )}

        {/* Timeline */}
        {booking.timeline?.length > 0 && (
          <div className="bd-card" style={{ marginTop: '8px' }}>
            <h3>📅 Timeline</h3>
            {booking.timeline.map((t, i) => (
              <div key={i} style={{
                display: 'flex', gap: '12px', paddingBottom: '12px',
                borderBottom: i < booking.timeline.length - 1 ? '1px solid #f1f5f9' : 'none'
              }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: '#C8860A', marginTop: '6px', flexShrink: 0
                }} />
                <div>
                  <p style={{ fontWeight: 600, fontSize: '14px', margin: 0, textTransform: 'capitalize' }}>{t.status}</p>
                  <p style={{ color: '#64748b', fontSize: '13px', margin: '2px 0' }}>{t.message}</p>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>
                    {new Date(t.timestamp).toLocaleString('hi-IN')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}